/**
 * L3 明细的**内容体**（`AC-Q4.3` · 批 `111` 抽出）
 *
 * ## 🔴 为什么把它从抽屉里抽出来（这是本批唯一的结构改动）
 *
 * `AC-Q4.3` 的明细原来**只有一个载体**：从卡片点「明细」弹出的抽屉。
 * 深链接进不去（`/debts/<id>` 无路由），而"抽屉"在 MUI 里走 **Portal** ⇒
 * 静态渲染拿不到它的内容（铁律 121）⇒ 连"三类行画对了没"都没法在端侧取证。
 *
 * ⇒ 拆成 **内容体 + 两种外壳**：
 *
 * | 外壳 | 文件 | 场景 |
 * |---|---|---|
 * | 抽屉 | `ContraTimelineDrawer.tsx` | 卡片点「明细」（原地看，不跳页） |
 * | 页面 | `pages/ContraDetailPage.tsx` | `/debts/<contraId>` 深链接（可收藏 / 可分享） |
 *
 * 🔴 **两处必须共用本组件** —— 一旦谁自己再写一遍三类行的渲染，就会出现
 * "抽屉更正过、页面没跟上"的第二套口径（铁律 24）。本批的端侧 spec 用
 * 「页面 HTML 里逐字节包含本组件单独渲染的 HTML」来钉这一条。
 *
 * ## 三类行（服务端合并好、按时间倒序下发，本组件**只画**）
 *
 * | `kind` | 说明 |
 * |---|---|
 * | `txn` | 交易行（该账户上的腿；同一笔流水的多条腿已被服务端合并成 1 行） |
 * | `settlement` | 结算行（本金的唯一痕迹） |
 * | `pendingPeriod` | 待还期次行 —— 🔴 **服务端只下发 `pending`**（`AC-Q5.2`），本组件**不补**已还/已作废的 |
 *
 * ## 🔴 三条纪律
 *
 * 1. **方向与色彩不问本组件**：`direction` 由服务端给（`toContra` / `fromContra`），
 *    金额交给 `<Amount>`（正负配色全站一致 —— `AC-Q4.6`）；
 * 2. **不自己排时间**：服务端已按 `date desc → kind → 金额 → id` 稳定序（同日后稳定，铁律 82）；
 * 3. **标题用服务端下发的 `label`** —— 端侧不维护"第几期 / 收回 / 还款"的映射表。
 */

import type { ReactElement, ReactNode } from 'react';
import { Alert, Chip, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { formatMoneyText, type Cents } from '@jiaying/shared';
import { getContraTimeline } from '../api/endpoints.js';
import { timelineErrorView } from '../features/contra-timeline.js';
import { Amount } from './Amount.js';
import { Loading } from './Loading.js';

const KIND_LABEL: Readonly<Record<'txn' | 'settlement' | 'pendingPeriod', string>> = {
  txn: '流水',
  settlement: '结算',
  pendingPeriod: '待还',
};

export interface ContraTimelineBodyProps {
  accountId: string;
  /**
   * 右上角的**外壳动作**（抽屉给「整页」+「关闭」；页面不给）。
   *
   * 🔴 只放动作、不放数据 —— 内容体本身在两种外壳下必须完全一致，否则
   * 「页面与抽屉逐行一致」的断言会变成两个组件的对比。
   */
  actions?: ReactNode;
}

export function ContraTimelineBody({ accountId, actions }: ContraTimelineBodyProps): ReactElement {
  const q = useQuery({
    queryKey: ['contraTimeline', accountId],
    queryFn: () => getContraTimeline(accountId),
  });

  // 🔴 错误态文案的唯一判定处在 `features/contra-timeline.ts`（纯函数，可直接测）
  const err = q.isError ? timelineErrorView(q.error) : null;

  return (
    <Stack sx={{ p: 2 }} spacing={1} data-testid="contra-timeline-body">
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap data-testid="contra-timeline-title">
            {q.data?.accountName ?? '明细'}
          </Typography>
          {q.data?.counterparty !== null && q.data?.counterparty !== undefined && (
            <Typography variant="caption" color="text.secondary" noWrap>
              对手方：{q.data.counterparty}
            </Typography>
          )}
        </Stack>
        {actions !== undefined && <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>{actions}</Stack>}
      </Stack>

      {q.data !== undefined && (
        <Typography variant="caption" color="text.secondary" data-testid="contra-timeline-summary">
          当前余额 {formatMoneyText(q.data.balanceCents as Cents, q.data.currency)} · 共 {q.data.rows.length} 行
        </Typography>
      )}

      <Divider />

      {q.isPending && <Loading />}
      {err !== null && (
        <Alert severity={err.severity} data-testid="contra-timeline-error">
          {err.text}
        </Alert>
      )}

      {q.data !== undefined && q.data.rows.length === 0 && (
        <Alert severity="info" data-testid="contra-timeline-empty">
          还没有任何记录。收回 / 还款 / 借出之后，这里会按时间倒序列出三类行。
        </Alert>
      )}

      <List dense disablePadding>
        {q.data?.rows.map((r) => (
          <ListItem
            key={`${r.kind}-${r.transactionId ?? r.settlementId ?? String(r.periodIndex)}-${r.date}`}
            divider
            disablePadding
            sx={{ py: 1, px: 0 }}
            data-testid={`contra-timeline-row-${r.kind}`}
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={KIND_LABEL[r.kind]} />
                  <span>{r.label}</span>
                </Stack>
              }
              secondary={r.note === null ? r.date : `${r.date} · ${r.note}`}
            />
            <Amount
              cents={(r.direction === 'toContra' ? r.amount : -(r.amount as number)) as never}
              currency={q.data?.currency ?? 'CNY'}
              sx={{ ml: 1, flexShrink: 0, fontSize: '0.875rem' }}
            />
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
